// Quote-request modal — posts to the Cloudflare Worker configured in theme settings.
// Window globals used:
//   window.quoteEndpoint     — full URL, e.g. https://quote-api.example.workers.dev/api/quote
//   window.openQuoteModal()  — defined at the bottom of this file
//   window.onQuoteTurnstileSuccess(token) — populated by the Turnstile widget callback

if (!customElements.get('quote-request-modal')) {
  customElements.define(
    'quote-request-modal',
    class QuoteRequestModal extends HTMLElement {
      constructor () {
        super()

        this.overlay = this.querySelector('.quote-modal__overlay')
        this.container = this.querySelector('.quote-modal__container')
        this.closeButton = this.querySelector('.quote-modal__close')
        this.cancelButton = this.querySelector('.quote-cancel-btn')
        this.form = this.querySelector('[data-quote-form]')
        this.cartItemsContainer = this.querySelector('[data-cart-items]')
        this.successMessage = this.querySelector('.quote-modal__success')
        this.submitButton = this.querySelector('.quote-submit-btn')
        this.errorContainer = this.querySelector('[data-quote-error]')

        this.isOpen = false
        this.showingSuccess = false
        this.cartData = null

        this.open = this.open.bind(this)
        this.close = this.close.bind(this)
        this.handleSubmit = this.handleSubmit.bind(this)
        this.handleOverlayClick = this.handleOverlayClick.bind(this)
        this.handleEscapeKey = this.handleEscapeKey.bind(this)

        if (this.closeButton) this.closeButton.addEventListener('click', this.close)
        if (this.cancelButton) this.cancelButton.addEventListener('click', this.close)
        if (this.overlay) this.overlay.addEventListener('click', this.handleOverlayClick)
        if (this.form) this.form.addEventListener('submit', this.handleSubmit)

        document.addEventListener('cart:updated', () => {
          if (this.isOpen) this.loadCartItems()
        })

        const successBtn = this.successMessage && this.successMessage.querySelector('.quote-success-btn')
        if (successBtn) {
          successBtn.addEventListener('click', async () => {
            try { await fetch('/cart/clear.js', { method: 'POST' }) } catch (_) {}
            window.location.href = '/'
          })
        }
      }

      open () {
        this.isOpen = true
        this.classList.add('is-active')
        document.body.style.overflow = 'hidden'
        document.addEventListener('keydown', this.handleEscapeKey)
        this.loadCartItems()
      }

      close () {
        if (this.showingSuccess) return
        this.isOpen = false
        this.classList.remove('is-active')
        document.body.style.overflow = ''
        document.removeEventListener('keydown', this.handleEscapeKey)
        if (this.form) {
          this.form.reset()
          this.form.style.display = ''
        }
        if (this.successMessage) this.successMessage.style.display = 'none'
        this.clearError()
      }

      handleOverlayClick (e) { if (e.target === this.overlay) this.close() }
      handleEscapeKey (e) { if (e.key === 'Escape' && this.isOpen) this.close() }

      async loadCartItems () {
        try {
          const response = await fetch('/cart.js')
          const cart = await response.json()

          if (!cart.items || cart.items.length === 0) {
            this.cartItemsContainer.innerHTML = '<p class="quote-cart-empty">Your cart is empty.</p>'
            this.cartData = cart
            return
          }

          let html = '<div class="quote-cart-list">'
          for (const item of cart.items) {
            html += `
              <div class="quote-cart-item">
                <div class="quote-cart-item__image">
                  <img src="${escapeHtml(item.image || '')}" alt="${escapeHtml(item.product_title)}" loading="lazy">
                </div>
                <div class="quote-cart-item__details">
                  <div class="quote-cart-item__title">${escapeHtml(item.product_title)}</div>
                  ${item.variant_title ? `<div class="quote-cart-item__variant">${escapeHtml(item.variant_title)}</div>` : ''}
                  ${item.sku ? `<div class="quote-cart-item__sku">SKU: ${escapeHtml(item.sku)}</div>` : ''}
                  <div class="quote-cart-item__quantity">Qty: ${item.quantity}</div>
                </div>
              </div>`
          }
          html += '</div>'
          this.cartItemsContainer.innerHTML = html

          this.cartData = cart
        } catch (error) {
          console.error('Error loading cart:', error)
          this.cartItemsContainer.innerHTML = '<p class="quote-cart-error">Error loading cart items.</p>'
        }
      }

      async handleSubmit (e) {
        e.preventDefault()
        this.clearError()

        if (!this.form.checkValidity()) {
          this.form.reportValidity()
          return
        }
        if (!window.quoteEndpoint) {
          this.showError('Quote system is not configured. Please contact us directly.')
          return
        }
        if (!this.cartData || !this.cartData.items || this.cartData.items.length === 0) {
          this.showError('Your cart is empty.')
          return
        }

        const fd = new FormData(this.form)
        const turnstileToken = fd.get('cf_turnstile_token') || ''
        if (!turnstileToken) {
          this.showError('Please complete the verification challenge before submitting.')
          return
        }

        const payload = {
          turnstile_token: String(turnstileToken),
          quote: {
            quote_name: fd.get('quotename'),
            first_name: fd.get('firstname'),
            last_name: fd.get('lastname'),
            company: fd.get('company'),
            email: fd.get('email'),
            telephone: fd.get('telephone'),
            street_address: fd.get('streetaddress'),
            city: fd.get('city'),
            zipcode: fd.get('zipcode'),
            country: fd.get('country'),
            event_start: fd.get('event_start'),
            event_end: fd.get('event_end'),
            venue_name: fd.get('venue_name'),
            description: fd.get('description') || ''
          },
          items: this.cartData.items.map(item => ({
            product_id: `gid://shopify/Product/${item.product_id}`,
            variant_id: item.variant_id ? `gid://shopify/ProductVariant/${item.variant_id}` : null,
            quantity: item.quantity
          }))
        }

        this.setLoading(true)
        try {
          const res = await fetch(window.quoteEndpoint, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify(payload)
          })
          let body = null
          try { body = await res.json() } catch (_) {}
          if (!res.ok || !body || !body.ok) {
            const msg = (body && body.error && body.error.message) || `Request failed (${res.status}).`
            throw new Error(msg)
          }
          this.showSuccess()
        } catch (err) {
          console.error('Quote submission failed:', err)
          this.showError(err.message || 'Something went wrong. Please try again.')
          this.resetTurnstile()
        } finally {
          this.setLoading(false)
        }
      }

      setLoading (isLoading) {
        const btnText = this.submitButton.querySelector('.btn-text')
        const btnLoading = this.submitButton.querySelector('.btn-loading')
        if (btnText) btnText.style.display = isLoading ? 'none' : ''
        if (btnLoading) btnLoading.style.display = isLoading ? 'inline' : 'none'
        this.submitButton.disabled = !!isLoading
      }

      showSuccess () {
        this.showingSuccess = true
        this.form.style.display = 'none'
        if (this.successMessage) this.successMessage.style.display = 'block'
      }

      showError (msg) {
        if (!this.errorContainer) return alert(msg)
        this.errorContainer.textContent = msg
        this.errorContainer.style.display = 'block'
      }

      clearError () {
        if (!this.errorContainer) return
        this.errorContainer.textContent = ''
        this.errorContainer.style.display = 'none'
      }

      resetTurnstile () {
        if (window.turnstile && typeof window.turnstile.reset === 'function') {
          try { window.turnstile.reset() } catch (_) {}
        }
        const hidden = this.form.querySelector('input[name="cf_turnstile_token"]')
        if (hidden) hidden.value = ''
      }
    }
  )
}

window.openQuoteModal = function () {
  const modal = document.querySelector('quote-request-modal')
  if (modal) modal.open()
}

// Turnstile widget callbacks — populate the hidden input so the form picks up the token.
window.onQuoteTurnstileSuccess = function (token) {
  const input = document.querySelector('quote-request-modal input[name="cf_turnstile_token"]')
  if (input) input.value = token
}
window.onQuoteTurnstileError = function () {
  const input = document.querySelector('quote-request-modal input[name="cf_turnstile_token"]')
  if (input) input.value = ''
}
window.onQuoteTurnstileExpired = function () {
  const input = document.querySelector('quote-request-modal input[name="cf_turnstile_token"]')
  if (input) input.value = ''
}

function escapeHtml (s) {
  if (s == null) return ''
  return String(s).replace(/[&<>"']/g, ch => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[ch]))
}
